"use client";

import { Dialog, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { cn } from "~/lib/utils";
import { useTranslator } from "~/i18n/provider";

/** Scoped styling for the sanitised size-guide HTML (tables are the norm). */
const SIZE_GUIDE_PROSE = cn(
  "max-w-none text-sm leading-relaxed text-fg-muted",
  "[&_p]:my-3 [&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-fg",
  "[&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:font-semibold [&_h3]:text-fg",
  "[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1",
  "[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-left",
  "[&_th]:border [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:font-semibold [&_th]:text-fg",
  "[&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-1.5",
);

/**
 * Brand/product size-guide modal (P3-06). Content arrives pre-sanitised as HTML
 * from the projection (`renderDescriptionHtml`, server-side), so this ships no
 * sanitiser. Focus trap, Esc-to-close and focus restoration are handled by the
 * shared {@link Dialog}.
 */
export function SizeGuideDialog({
  open,
  onOpenChange,
  html,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  html: string;
}) {
  const t = useTranslator();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      ariaLabel={t("pdp.sizeGuide")}
      className="max-w-2xl"
    >
      <DialogHeader>
        <DialogTitle>{t("pdp.sizeGuide")}</DialogTitle>
      </DialogHeader>
      <div className="max-h-[70vh] overflow-y-auto">
        <div
          className={SIZE_GUIDE_PROSE}
          data-testid="size-guide-content"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </Dialog>
  );
}
